import { ColumnDropdownOption, DomainConditionOptions, InsightsDropdownGroup } from '../types'
import { FEAT_PUSHLY_AI, FLAG_INSIGHTS_CALCULATE_DELIVERIES } from '../../../constants'
import { ReportConfigurationType } from '../enums/report-configuration-type'
import { ReportConfigurationLevel } from '../enums/report-configuration-level'
import { PlatformLevel } from '../enums/platform-level'
import { ensureBreakdown, ensureDomain, ensureOrg } from '../helpers/conditions'
import { ReportConfigurationBreakdown } from '../enums/report-configuration-breakdown'

const ensureDomainFlag =
    (flag: string) =>
    ({ domain }: DomainConditionOptions) => {
        return domain.flags.includes(flag)
    }

const domainHasCalcDeliveriesFlag = ensureDomainFlag(FLAG_INSIGHTS_CALCULATE_DELIVERIES)

const deliveryColumns: ColumnDropdownOption[] = [
    {
        value: 'DELIVERIES',
        label: 'Deliveries',
        condition: (columnOpts) => ensureDomain(columnOpts) && domainHasCalcDeliveriesFlag(columnOpts),
    },
    {
        value: 'DELIVERY_RATE',
        label: 'Impression Rate',
        condition: (columnOpts) => ensureDomain(columnOpts) && domainHasCalcDeliveriesFlag(columnOpts),
    },
]

export type ColumnDropdownKey = `${ReportConfigurationType}_${ReportConfigurationLevel}`

export const COLUMN_DROPDOWN_OPTIONS: Partial<
    Record<ColumnDropdownKey, InsightsDropdownGroup<ColumnDropdownOption>[]>
> = {
    DELIVERY_DOMAIN: [
        {
            label: 'Dimensions',
            options: [
                {
                    value: 'DOMAIN.DISPLAY_NAME',
                    label: 'Domain Name',
                    default: true,
                    condition: (opts) => ensureOrg(opts),
                },
                {
                    value: 'BUTTON.ID',
                    label: 'Button ID',
                    condition: ensureBreakdown(ReportConfigurationBreakdown.BUTTON),
                },
                {
                    value: 'BUTTON.TYPE',
                    label: 'Button Type',
                    condition: ensureBreakdown(ReportConfigurationBreakdown.BUTTON),
                },
                {
                    value: 'BUTTON.LABEL',
                    label: 'Button Label',
                    condition: ensureBreakdown(ReportConfigurationBreakdown.BUTTON),
                },
                {
                    value: 'BUTTON.LANDING_URL',
                    label: 'Button URL',
                    condition: ensureBreakdown(ReportConfigurationBreakdown.BUTTON),
                },
            ],
        },
        {
            label: 'Measures',
            options: [
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'CTR', default: true },
                ...deliveryColumns,
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'NOTIFICATIONS_SENT', label: 'Notifications Sent' },
                { value: 'CPS', label: 'Clicks Per Send (CPS)' },
                { value: 'PURCHASES', label: 'Purchases', condition: ensureDomain },
                { value: 'PURCHASE_AMOUNT', label: 'Purchase Amount', condition: ensureDomain },
            ],
        },
    ],
    DELIVERY_NOTIFICATION: [
        {
            label: 'Dimensions',
            options: [
                { value: 'TEST.NAME', label: 'A/B Test Name', condition: ensureDomain },
                { value: 'NOTIFICATION.WEIGHT', label: 'A/B Test Weight', condition: ensureDomain },
                {
                    value: 'DOMAIN.ID',
                    label: 'Domain ID',
                    condition: (opts) =>
                        ensureOrg(opts) &&
                        (ensureBreakdown(ReportConfigurationBreakdown.DOMAIN)(opts) ||
                            ensureBreakdown(ReportConfigurationBreakdown.DOMAIN_CHANNEL)(opts) ||
                            ensureBreakdown(ReportConfigurationBreakdown.DOMAIN_PLACEMENT)(opts) ||
                            ensureBreakdown(ReportConfigurationBreakdown.DOMAIN_CHANNEL_PLACEMENT)(opts)),
                },
                {
                    value: 'DOMAIN.DISPLAY_NAME',
                    label: 'Domain Name',
                    condition: (opts) =>
                        ensureOrg(opts) &&
                        (ensureBreakdown(ReportConfigurationBreakdown.DOMAIN)(opts) ||
                            ensureBreakdown(ReportConfigurationBreakdown.DOMAIN_CHANNEL)(opts) ||
                            ensureBreakdown(ReportConfigurationBreakdown.DOMAIN_PLACEMENT)(opts) ||
                            ensureBreakdown(ReportConfigurationBreakdown.DOMAIN_CHANNEL_PLACEMENT)(opts)),
                },
                { value: 'NOTIFICATION.AUDIENCES', label: 'Audiences', default: true },
                { value: 'NOTIFICATION.BADGE_URL', label: 'Badge' },
                { value: 'NOTIFICATION.BODY', label: 'Body', default: true },
                {
                    value: 'BUTTON.ID',
                    label: 'Button ID',
                    condition: (opts) =>
                        ensureDomain(opts) && !!ensureBreakdown(ReportConfigurationBreakdown.BUTTON)?.(opts),
                },
                {
                    value: 'BUTTON.TYPE',
                    label: 'Button Type',
                    condition: (opts) =>
                        ensureDomain(opts) && !!ensureBreakdown(ReportConfigurationBreakdown.BUTTON)?.(opts),
                },
                {
                    value: 'BUTTON.LABEL',
                    label: 'Button Label',
                    condition: (opts) =>
                        ensureDomain(opts) && !!ensureBreakdown(ReportConfigurationBreakdown.BUTTON)?.(opts),
                },
                {
                    value: 'BUTTON.LANDING_URL',
                    label: 'Button URL',
                    condition: (opts) =>
                        ensureDomain(opts) && !!ensureBreakdown(ReportConfigurationBreakdown.BUTTON)?.(opts),
                },
                { value: 'CAMPAIGN.ID', label: 'Journey ID', condition: ensureDomain },
                { value: 'CAMPAIGN.NAME', label: 'Journey Name', condition: ensureDomain },
                { value: 'STEP.ID', label: 'Journey Step ID', condition: ensureDomain },
                { value: 'STEP.NAME', label: 'Journey Step Name', condition: ensureDomain },
                { value: 'NOTIFICATION.CONTENT_SOURCE', label: 'Content Source' },
                {
                    value: 'NOTIFICATION.AI_CONTENT_TONE',
                    label: 'AI Content Tone',
                    condition: (opts) => ensureDomain(opts) && ensureDomainFlag(FEAT_PUSHLY_AI)(opts),
                },
                { value: 'NOTIFICATION.CREATED_BY', label: 'Created By' },
                { value: 'NOTIFICATION.DELIVERY_STRATEGY', label: 'Delivery Strategy' },
                { value: 'NOTIFICATION.ICON_URL', label: 'Icon' },
                { value: 'NOTIFICATION.IMAGE_URL', label: 'Image', default: true },
                { value: 'NOTIFICATION.KEYWORDS', label: 'Keywords' },
                { value: 'NOTIFICATION.LANDING_URL', label: 'Landing URL' },
                { value: 'NOTIFICATION.LIFESPAN', label: 'Lifespan' },
                { value: 'NOTIFICATION.SCHEDULED_TIME', label: 'Scheduled Time', default: true },
                { value: 'NOTIFICATION_SOURCE', label: 'Source' },
                { value: 'NOTIFICATION.TARGETED_CHANNELS', label: 'Targeted Channels', default: false },
                { value: 'NOTIFICATION.TITLE', label: 'Title', default: true },
            ],
        },
        {
            label: 'Measures',
            options: [
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'CTR', default: true },
                { value: 'CPS', label: 'Clicks Per Send (CPS)', default: true },
                ...deliveryColumns,
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'PURCHASES', label: 'Purchases', condition: ensureDomain },
                { value: 'PURCHASE_AMOUNT', label: 'Purchase Amount', condition: ensureDomain },
                { value: 'PURCHASE_RATE_DECIMAL', label: 'Purchase Rate', condition: ensureDomain },
            ],
        },
    ],
    DELIVERY_TEST: [
        {
            label: 'Dimensions',
            options: [
                { value: 'TEST.CREATED_BY', label: 'Created By' },
                { value: 'TEST.NAME', label: 'A/B Test Name', default: true },
            ],
        },
        {
            label: 'Measures',
            options: [
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'CTR', default: true },
                ...deliveryColumns,
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
            ],
        },
    ],
    DELIVERY_CAMPAIGN: [
        {
            label: 'Dimensions',
            options: [
                { value: 'CAMPAIGN.NAME', label: 'Journey Name', default: true },
                { value: 'CAMPAIGN.STATUS', label: 'Journey Status' },
                { value: 'CAMPAIGN.DATE_START', label: 'Start Date' },
                { value: 'CAMPAIGN.DATE_END', label: 'End Date' },
                {
                    value: 'STEP.NAME',
                    label: 'Step Name',
                    condition: ensureBreakdown(ReportConfigurationBreakdown.STEP),
                },
                {
                    value: 'STEP.TYPE',
                    label: 'Step Type',
                    condition: ensureBreakdown(ReportConfigurationBreakdown.STEP),
                },
                {
                    value: 'SPLIT_TEST.NAME',
                    label: 'Split Test Name',
                    default: true,
                    condition: (opts) =>
                        ensureBreakdown(ReportConfigurationBreakdown.VARIANT)(opts) ||
                        ensureBreakdown(ReportConfigurationBreakdown.SPLIT_TEST)(opts),
                },
                {
                    value: 'VARIANT.NAME',
                    label: 'Split Test Variant Name',
                    condition: ensureBreakdown(ReportConfigurationBreakdown.VARIANT),
                },
                {
                    value: 'VARIANT.PERCENTAGE',
                    label: 'Split Test Variant %',
                    condition: ensureBreakdown(ReportConfigurationBreakdown.VARIANT),
                },
            ],
        },
        {
            label: 'Measures',
            options: [
                { value: 'ENTRANCES', label: 'Entrants', default: true },
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'CTR', default: true },
                { value: 'CPS', label: 'CPS' },
                ...deliveryColumns,
                { value: 'PURCHASES', label: 'Purchases' },
                { value: 'PURCHASE_AMOUNT', label: 'Purchase Amount' },
            ],
        },
    ],
    DELIVERY_SEGMENT: [
        {
            label: 'Dimensions',
            options: [
                { value: 'SEGMENT.NAME', label: 'Segment Name', default: true },
                { value: 'SEGMENT_LAST_TARGETED_DATE_TZ', label: 'Last Targeted Date', default: true },
            ],
        },
        {
            label: 'Measures',
            options: [
                ...deliveryColumns,
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'CTR', default: true },
                { value: 'NOTIFICATIONS_SENT', label: 'Notifications Sent', default: true },
                { value: 'CPS', label: 'Clicks Per Send (CPS)', default: true },
                { value: 'SIZE', label: 'Segment Size' },
            ],
        },
    ],
    DELIVERY_KEYWORD: [
        {
            label: 'Dimensions',
            options: [{ value: 'KEYWORD', label: 'Keyword', default: true }],
        },
        {
            label: 'Measures',
            options: [
                ...deliveryColumns,
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'CTR', default: true },
                { value: 'NOTIFICATIONS_SENT', label: 'Notifications Sent', default: true },
            ],
        },
    ],
    SUBSCRIBER_ORGANIZATION: [
        {
            label: 'Measures',
            options: [
                { value: 'SUBSCRIPTIONS', label: 'Subscriptions (Permissions Accepted)', default: true },
                { value: 'SUBSCRIPTION_RATE', label: 'Subscription Rate', default: true },
                { value: 'PERMISSIONS_SHOWN', label: 'Permissions Shown', default: true },
                { value: 'PERMISSIONS_DENIED', label: 'Permissions Denied' },
                { value: 'PERMISSIONS_DISMISSED', label: 'Permissions Dismissed' },
                { value: 'PROMPTS_SHOWN', label: '2-Step Prompts Shown (Unique)' },
            ],
        },
    ],
    SUBSCRIBER_DOMAIN: [
        {
            label: 'Dimensions',
            options: [
                { value: 'DOMAIN.DISPLAY_NAME', label: 'Domain Name', default: true, condition: ensureOrg },
                { value: 'DOMAIN.ID', label: 'Domain ID', default: true, condition: ensureOrg },
            ],
        },
        {
            label: 'Measures',
            options: [
                { value: 'PROMPTS_SHOWN', label: '2-Step Prompts Shown (Unique)' },
                { value: 'PROMPTS_SHOWN_NON_UNIQUE', label: '2-Step Prompts Shown', condition: ensureDomain },
                { value: 'PROMPTS_DISMISSED', label: '2-Step Prompts Dismissed (Unique)' },
                { value: 'PROMPTS_DISMISSED_NON_UNIQUE', label: '2-Step Prompts Dismissed', condition: ensureDomain },
                { value: 'PROMPTS_ACCEPTED', label: '2-Step Prompts Accepted', condition: ensureDomain },
                { value: 'PROMPT_ACCEPT_RATE', label: '2-Step Prompt Accept Rate', condition: ensureDomain },
                { value: 'PERMISSIONS_SHOWN', label: 'Permissions Shown', default: true },
                { value: 'PERMISSIONS_DENIED', label: 'Permissions Denied' },
                { value: 'PERMISSIONS_DISMISSED', label: 'Permissions Dismissed' },
                { value: 'SUBSCRIPTIONS', label: 'Subscriptions (Permissions Accepted)', default: true },
                { value: 'SUBSCRIPTION_RATE', label: 'Subscription Rate', default: true },
            ],
        },
    ],
    SUBSCRIBER_PROMPT: [
        {
            label: 'Dimensions',
            options: [
                { value: 'TEST.ID', label: 'A/B Test ID' },
                { value: 'TEST.NAME', label: 'A/B Test Name' },
                { value: 'PROMPT.NAME', label: 'Name', default: true },
                { value: 'PROMPT.PRIORITY', label: 'Priority' },
                { value: 'PROMPT.STYLE', label: 'Style' },
                { value: 'PROMPT.VISITOR_CONDITIONS', label: 'Visitor Conditions' },
            ],
        },
        {
            label: 'Measures',
            options: [
                { value: 'PROMPTS_SHOWN', label: '2-Step Prompts Shown (Unique)' },
                { value: 'PROMPTS_SHOWN_NON_UNIQUE', label: '2-Step Prompts Shown' },
                { value: 'PROMPTS_DISMISSED', label: '2-Step Prompts Dismissed (Unique)' },
                { value: 'PROMPTS_DISMISSED_NON_UNIQUE', label: '2-Step Prompts Dismissed' },
                { value: 'PROMPTS_ACCEPTED', label: '2-Step Prompts Accepted' },
                { value: 'PROMPT_ACCEPT_RATE', label: '2-Step Prompt Accept Rate' },
                { value: 'PERMISSIONS_SHOWN', label: 'Permissions Shown', default: true },
                { value: 'PERMISSIONS_DENIED', label: 'Permissions Denied' },
                { value: 'PERMISSIONS_DISMISSED', label: 'Permissions Dismissed' },
                { value: 'SUBSCRIPTIONS', label: 'Subscriptions (Permissions Accepted)', default: true },
                { value: 'SUBSCRIPTION_RATE', label: 'Subscription Rate', default: true },
            ],
        },
    ],
    SUBSCRIBER_TEST: [
        {
            label: 'Dimensions',
            options: [{ value: 'TEST.NAME', label: 'A/B Test Name', default: true }],
        },
        {
            label: 'Measures',
            options: [
                { value: 'PROMPTS_SHOWN', label: '2-Step Prompts Shown (Unique)' },
                { value: 'PROMPTS_SHOWN_NON_UNIQUE', label: '2-Step Prompts Shown' },
                { value: 'PROMPTS_DISMISSED', label: '2-Step Prompts Dismissed (Unique)' },
                { value: 'PROMPTS_DISMISSED_NON_UNIQUE', label: '2-Step Prompts Dismissed' },
                { value: 'PROMPTS_ACCEPTED', label: '2-Step Prompts Accepted' },
                { value: 'PROMPT_ACCEPT_RATE', label: '2-Step Prompt Accept Rate' },
                { value: 'PERMISSIONS_SHOWN', label: 'Permissions Shown', default: true },
                { value: 'PERMISSIONS_DENIED', label: 'Permissions Denied' },
                { value: 'PERMISSIONS_DISMISSED', label: 'Permissions Dismissed' },
                { value: 'SUBSCRIPTIONS', label: 'Subscriptions (Permissions Accepted)', default: true },
                { value: 'SUBSCRIPTION_RATE', label: 'Subscription Rate', default: true },
            ],
        },
    ],
    MONTHLY_UNIQUES_DOMAIN: [
        {
            label: 'Measures',
            options: [
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'CTR', default: true },
                { value: 'PERMISSIONS_SHOWN', label: 'Permissions Shown', default: true },
                { value: 'SUBSCRIPTIONS', label: 'Subscriptions', default: true },
                { value: 'SUBSCRIPTION_RATE', label: 'Subscription Rate', default: true },
            ],
        },
    ],
    PUB_NETWORK_DOMAIN: [
        {
            label: 'Measures',
            options: [
                { value: 'PAID_CLICKS', label: 'Clicks', default: true },
                { value: 'PAID_CTR', label: 'CTR', default: true },
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'REVENUE', label: 'Revenue', default: true },
            ],
        },
    ],
    APP_MESSAGE_DOMAIN: [
        {
            label: 'Measures',
            options: [
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'Total CTR', default: true },
            ],
        },
    ],
    APP_MESSAGE_APP_MESSAGE: [
        {
            label: 'Dimensions',
            options: [
                { value: 'APP_MESSAGE.NAME', label: 'Name', default: true },
                { value: 'APP_MESSAGE.STYLE', label: 'Style' },
                { value: 'APP_MESSAGE.RUN_DATE_START', label: 'Run Date Start' },
                { value: 'APP_MESSAGE.RUN_DATE_END', label: 'Run Date End' },
                { value: 'APP_MESSAGE.RUN_DATE_TIME_ZONE', label: 'Run Date Time Zone' },
                { value: 'APP_MESSAGE.AUDIENCES', label: 'Audiences' },
            ],
        },
        {
            label: 'Measures',
            options: [
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'Total CTR', default: true },
            ],
        },
    ],
    LIVE_ACTIVITY_DOMAIN: [
        {
            label: 'Measures',
            options: [
                { value: 'STARTS', label: 'Starts', default: true },
                { value: 'UPDATES_SENT', label: 'Updates Sent', default: true },
            ],
        },
    ],
    LIVE_ACTIVITY_ACTIVITY: [
        {
            label: 'Measures',
            options: [
                { value: 'STARTS', label: 'Starts', default: true },
                { value: 'UPDATES_SENT', label: 'Updates Sent', default: true },
            ],
        },
    ],
    DELIVERY_ORGANIZATION: [
        {
            label: 'Measures',
            options: [
                { value: 'NOTIFICATIONS_SENT', label: 'Notifications Sent' },
                { value: 'IMPRESSIONS', label: 'Impressions', default: true },
                { value: 'CLICKS', label: 'Clicks', default: true },
                { value: 'CTR', label: 'CTR', default: true },
                { value: 'CPS', label: 'Clicks Per Send (CPS)' },
            ],
        },
    ],
}
